import { useFormikContext } from 'formik';
import React, { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EventBookingFormValues, SupportedLanguages } from '@carlito/bookito-client';
import { useActiveLanguage } from '@carlito/api';
import { SUPPORTED_LANGUAGE_CODES } from '@carlito/intl';
import { BookitoBookingFormValues } from '../shared/types';
import { DateInputWithKeyboard } from '../../KeyBoard/DateInputWithKeyboard';
import TextInputWithKeyboard from '../../KeyBoard/TextInputWithKeyboard';
import RoomNumberAndBookingRefField from './RoomNumberAndBookingRefField';

type Props = {
  hasPms?: boolean;
  isEditMode?: boolean;
  isKioskMode?: boolean;
  isBookingRefEnabled?: boolean;
  isRoomNrEnabled?: boolean;
  onValuesChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const supportedLanguages: Record<SupportedLanguages, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  pl: 'Polish',
  fr: 'French',
};

const ChooseLanguage = () => {
  const { getFieldProps, values, setValues } = useFormikContext<BookitoBookingFormValues>();
  const { activeLanguage } = useActiveLanguage();

  const sortedLanguages = useMemo(() => {
    const allLanguages = [...SUPPORTED_LANGUAGE_CODES];
    const otherLanguages = allLanguages.filter((lang) => lang !== activeLanguage);
    return [activeLanguage, ...otherLanguages];
  }, [activeLanguage]);

  const fieldName = `participants[0].communicationLanguage`;

  const { value: language, onBlur, onChange } = getFieldProps<SupportedLanguages>(fieldName);

  useEffect(() => {
    if (!language) {
      setValues({
        ...values,
        participants: [{ ...values?.participants?.[0], communicationLanguage: activeLanguage }],
      });
    }
  }, [activeLanguage]);

  return (
    <select
      className="text-sm rounded-md outline-none border border-gray-300 focus:border-primary-default focus:ring-2 focus:ring-primary-default focus:ring-offset-1"
      value={language}
      onChange={onChange}
      onBlur={onBlur}
      name={fieldName}
    >
      {sortedLanguages.map((key) => (
        <option key={key} value={key}>
          {supportedLanguages[key]}
        </option>
      ))}
    </select>
  );
};

export function BookitoParticipantForm({
  onValuesChange,
  hasPms,
  isEditMode = false,
  isBookingRefEnabled = true,
  isRoomNrEnabled = true,
  isKioskMode,
}: Props) {
  const { t } = useTranslation();
  const { values, handleBlur } = useFormikContext<EventBookingFormValues>();
  const roomNr = values.participants?.[0]?.roomNr; // bookedBy?.roomNr
  const bookingRef = values.bookingRef;
  const shouldHideExtendedForm = hasPms && (roomNr || bookingRef);
  const shouldShowBirthdateInput = shouldHideExtendedForm;
  const bookedBy = values.participants?.[0];

  return (
    <div className={'space-y-4 mb-4 w-full'}>
      {!isEditMode && (
        <React.Fragment>
          <RoomNumberAndBookingRefField
            isKioskMode={isKioskMode}
            isRoomNrEnabled={isRoomNrEnabled}
            isBookingRefEnabled={isBookingRefEnabled}
            onChange={onValuesChange}
          />
          {shouldShowBirthdateInput && (
            <DateInputWithKeyboard
              enabledKeyboard={isKioskMode}
              required
              id="birthDate"
              name="birthDate"
              type="date"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onValuesChange?.(e);
              }}
              onBlur={handleBlur}
              value={values.birthDate}
              placeholder={t('birthdate')}
              label={t('birthdate')}
              autoComplete={isKioskMode ? 'off' : 'on'}
            />
          )}
        </React.Fragment>
      )}

      {!shouldHideExtendedForm && (
        <>
          <TextInputWithKeyboard
            enabledKeyboard={isKioskMode}
            name="participants[0].name"
            label={t('name')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onValuesChange?.(e);
            }}
            type="text"
            required
            isAutoCompleteRequired={!isKioskMode}
          />

          <TextInputWithKeyboard
            enabledKeyboard={isKioskMode}
            name="participants[0].email"
            label="Email"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onValuesChange?.(e);
            }}
            type="email"
            isAutoCompleteRequired={!isKioskMode}
          />
          <TextInputWithKeyboard
            keyboardType={'Number'}
            canChangeKeyboardType={false}
            enabledKeyboard={isKioskMode}
            type="tel"
            name="participants[0].phoneNr"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onValuesChange?.(e);
            }}
            value={bookedBy?.phoneNr}
            label={t('phone-number')}
            isAutoCompleteRequired={!isKioskMode}
          />
          <ChooseLanguage />
        </>
      )}
    </div>
  );
}
